/**
 * domains/attribution/attributionEvaluator.ts — MER Algo Footprints v8.2.0
 *
 * Deterministic Forward Outcome Evaluator.
 *
 * Takes an AttributionSnapshot and an array of subsequent Candles and answers:
 *   - Did long trigger fire? (bar.high > longTriggerPrice)
 *   - Did short trigger fire? (bar.low < shortTriggerPrice)
 *   - Did invalidation occur? (bar.close breaches invalidation level)
 *   - Which happened first?
 *   - What was MFE / MAE over the window?
 *   - What were close deltas at 5/15/30 bar horizons?
 *
 * This is pure functional code — no side effects, no state, no I/O.
 * Every result is fully reproducible given the same inputs.
 */

import type {
  AttributionSnapshot,
  AttributionOutcome,
  EvaluatorConfig,
  HorizonResult,
  FirstEvent,
} from './types';
import type { Candle } from '../../shared/types';

// ─── Default horizons ─────────────────────────────────────────────────────────

const DEFAULT_HORIZONS = [5, 15, 30] as const;

// ─── Horizon key helper ───────────────────────────────────────────────────────

function horizonKey(bars: number): 'bars5' | 'bars15' | 'bars30' | null {
  if (bars === 5)  return 'bars5';
  if (bars === 15) return 'bars15';
  if (bars === 30) return 'bars30';
  return null;
}

// ─── MFE / MAE calculation ────────────────────────────────────────────────────
//
// For LONG bias: favorable = price above entry, adverse = price below entry
// For SHORT bias: favorable = price below entry, adverse = price above entry
// For NEUTRAL: favorable = abs(price - entry), adverse = 0 (no clear direction)

function computeExcursions(
  bars:         Candle[],
  entryPrice:   number,
  bias:         'long' | 'short' | 'neutral',
): { mfe: number; mae: number } {
  let mfe = 0;
  let mae = 0;

  for (const bar of bars) {
    if (bias === 'long') {
      const favorable = bar.high - entryPrice;
      const adverse   = entryPrice - bar.low;
      if (favorable > mfe) mfe = favorable;
      if (adverse   > mae) mae = adverse;
    } else if (bias === 'short') {
      const favorable = entryPrice - bar.low;
      const adverse   = bar.high - entryPrice;
      if (favorable > mfe) mfe = favorable;
      if (adverse   > mae) mae = adverse;
    } else {
      // neutral — track absolute deviation in either direction
      const upside   = bar.high - entryPrice;
      const downside = entryPrice - bar.low;
      const maxDev   = Math.max(upside, downside);
      if (maxDev > mfe) mfe = maxDev;
      // mae meaningless for neutral; leave 0
    }
  }

  return {
    mfe: Math.max(0, mfe),
    mae: Math.max(0, mae),
  };
}

// ─── Horizon result calculation ───────────────────────────────────────────────

function computeHorizonResult(
  bars:        Candle[],
  barCount:    number,
  entryPrice:  number,
  bias:        'long' | 'short' | 'neutral',
): HorizonResult | null {
  if (bars.length < barCount) return null;
  const window = bars.slice(0, barCount);
  const lastBar = window[window.length - 1];
  const { mfe, mae } = computeExcursions(window, entryPrice, bias);
  const closeDelta = lastBar.close - entryPrice;

  return {
    bars:       barCount,
    closePrice: lastBar.close,
    closeDelta,
    closePct:   entryPrice > 0 ? (closeDelta / entryPrice) * 100 : 0,
    mfe,
    mae,
  };
}

// ─── Main evaluator ───────────────────────────────────────────────────────────

/**
 * Evaluate a snapshot against subsequent price bars.
 *
 * @param snapshot  - The emitted state snapshot
 * @param bars      - Subsequent candles (must be in chronological order, bar[0] = first bar after snapshot)
 * @param config    - Optional trigger/invalidation prices and horizons
 */
export function evaluateOutcome(
  snapshot: AttributionSnapshot,
  bars:     Candle[],
  config?:  Partial<EvaluatorConfig>,
): AttributionOutcome {
  const horizons         = config?.horizons ?? [...DEFAULT_HORIZONS];
  const longTriggerPrice = config?.longTriggerPrice;
  const shortTriggerPrice = config?.shortTriggerPrice;
  const invalidationPrice = config?.invalidationPrice;
  const bias             = config?.directionalBias ?? snapshot.directionalBias;
  const entryPrice       = snapshot.entryPrice;

  let longTriggered  = false;
  let shortTriggered = false;
  let invalidated    = false;
  let firstEvent: FirstEvent  = 'none';
  let firstEventBar: number | null = null;

  const notes: string[] = [];

  // ── Scan bars in order — stop tracking first event after it's found ─────────
  for (let i = 0; i < bars.length; i++) {
    const bar = bars[i];

    // Long trigger: high breaks above the trigger level
    if (!longTriggered && longTriggerPrice != null && bar.high > longTriggerPrice) {
      longTriggered = true;
      if (firstEvent === 'none') {
        firstEvent    = 'longTrigger';
        firstEventBar = i;
      }
    }

    // Short trigger: low breaks below the trigger level
    if (!shortTriggered && shortTriggerPrice != null && bar.low < shortTriggerPrice) {
      shortTriggered = true;
      if (firstEvent === 'none') {
        firstEvent    = 'shortTrigger';
        firstEventBar = i;
      }
    }

    // Invalidation: close breaches invalidation level
    // Direction: if bias=long, invalidation is a close below the level
    //            if bias=short, invalidation is a close above the level
    //            if neutral, either direction
    if (!invalidated && invalidationPrice != null) {
      const breached =
        bias === 'long'    ? bar.close < invalidationPrice :
        bias === 'short'   ? bar.close > invalidationPrice :
        /* neutral */        (bar.close < invalidationPrice || bar.close > invalidationPrice);

      if (breached) {
        invalidated = true;
        if (firstEvent === 'none') {
          firstEvent    = 'invalidation';
          firstEventBar = i;
        }
      }
    }
  }

  if (!longTriggerPrice)   notes.push('longTriggerPrice not provided — longTriggered always false');
  if (!shortTriggerPrice)  notes.push('shortTriggerPrice not provided — shortTriggered always false');
  if (!invalidationPrice)  notes.push('invalidationPrice not provided — invalidated always false');
  if (bars.length === 0)   notes.push('no bars provided — all results are null/zero');

  // ── MFE / MAE across all available bars ──────────────────────────────────────
  const { mfe, mae } = computeExcursions(bars, entryPrice, bias);

  // ── Horizon results ───────────────────────────────────────────────────────────
  const horizonResults: AttributionOutcome['horizonResults'] = {};
  for (const h of horizons) {
    const key = horizonKey(h);
    if (!key) continue;
    const result = computeHorizonResult(bars, h, entryPrice, bias);
    if (result) horizonResults[key] = result;
  }

  return {
    snapshotId:    snapshot.snapshotId,
    symbol:        snapshot.symbol,
    state:         snapshot.state,
    evaluatedAt:   Date.now(),
    barsConsumed:  bars.length,

    longTriggered,
    shortTriggered,
    invalidated,
    firstEvent,
    firstEventBar,

    mfe,
    mae,

    horizonResults,
    notes,
  };
}

// ─── Convenience: evaluate with explicit trigger prices ───────────────────────

/**
 * Simplified evaluator: pass trigger/invalidation prices directly.
 * Most common usage pattern.
 */
export function evaluateOutcomeWithPrices(
  snapshot:          AttributionSnapshot,
  bars:              Candle[],
  longTriggerPrice?: number,
  shortTriggerPrice?: number,
  invalidationPrice?: number,
): AttributionOutcome {
  return evaluateOutcome(snapshot, bars, {
    horizons:          [...DEFAULT_HORIZONS],
    longTriggerPrice,
    shortTriggerPrice,
    invalidationPrice,
    directionalBias:   snapshot.directionalBias,
  });
}
